// FILE: src/pages/admin/AdminSupportLogPage.jsx
// "Log a phone or email request" (WP-6, MTO 10.1, 10.2, 11.2.1): when a
// student phones or emails, staff record it as a support request so it has
// the same 48-hour evidence, reference and audit trail as an online one.
// Staff find the student (GET /api/admin/users?search=), choose the kind of
// help and the channel, summarise the request, and may add what they told
// the student and mark it resolved:
//   POST /api/admin/support { userId, category, channel, description, reply?, resolve? } → 201 ticket
// The student is emailed the reference; the new request opens afterwards.
// A course-content reply needs the staff member's Digital Driving
// Instructor registration (403 DDI_REGISTRATION_REQUIRED, shown in words).
import React, { useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import fetchWithAuth from "../../utils/fetchWithAuth";
import { ErrorState, LoadingState, PageHeader, Section, visuallyHidden } from "../../components/ui";
import { personName } from "../../components/admin/format";
import { channelIcon, channelLabel } from "../../components/admin/supportSla";
import { TICKET_CATEGORIES, categoryIcon } from "../../components/support/ticketDisplay";
import { errorMessage } from "../../i18n/errors";
import { ticketCategoryHint, ticketCategoryLabel } from "../../i18n/labels";

const MAX_TEXT = 5000;
const LOGGED_CHANNELS = ["PHONE", "EMAIL"];
const ORDER = ["student", "category", "channel", "description"];

const STAFF_HINT = {
  TECHNICAL: "Signing in, loading lessons, video, sound or the student's device.",
  SERVICE: "The account, enrolment, schedule, records or anything administrative.",
  CONTENT: "What the lessons teach. A reply needs a Digital Driving Instructor registration."
};

const optionSx = (selected) => ({
  m: 0,
  alignItems: "flex-start",
  p: selected ? "15px" : "16px",
  borderRadius: 3,
  border: selected ? "2px solid" : "1px solid",
  borderColor: selected ? "primary.main" : "zun.inputBorder",
  bgcolor: selected ? "zun.primaryTint" : "background.paper",
  "&:hover": { borderColor: "primary.main" },
  "&:has(.Mui-focusVisible)": { outline: "3px solid", outlineColor: "zun.focus", outlineOffset: 2 },
  "& .MuiFormControlLabel-label": { minWidth: 0 }
});

function OptionCard({ value, selected, Icon, title, hint }) {
  return (
    <FormControlLabel
      value={value}
      control={<Radio sx={{ mt: -0.75, ml: -0.5 }} />}
      label={
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: hint ? 0.5 : 0 }}>
            <Icon aria-hidden sx={{ color: "primary.main" }} />
            <Typography component="span" sx={{ fontWeight: 650 }}>
              {title}
            </Typography>
          </Stack>
          {hint && (
            <>
              <Box component="span" sx={visuallyHidden}>
                .{" "}
              </Box>
              <Typography component="span" sx={{ display: "block", color: "text.secondary" }}>
                {hint}
              </Typography>
            </>
          )}
        </Box>
      }
      sx={optionSx(selected)}
    />
  );
}

function StudentPicker({ selected, onSelect, error, inputRef }) {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState({ users: null, loading: false, error: null, searched: "" });

  const search = async () => {
    const q = query.trim();
    if (!q) return;
    setResult((r) => ({ ...r, loading: true, error: null }));
    try {
      const data = await fetchWithAuth(`/api/admin/users?search=${encodeURIComponent(q)}&limit=10`);
      const users = (Array.isArray(data?.users) ? data.users : []).filter((u) => u.role !== "admin");
      setResult({ users, loading: false, error: null, searched: q });
    } catch (err) {
      setResult({ users: null, loading: false, error: err || "INTERNAL_ERROR", searched: q });
    }
  };

  return (
    <Section id="student" title="Student" description="Find the student by name or email.">
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "flex-start" }, mb: 2 }}>
        <TextField
          id="student-search"
          label="Name or email"
          value={query}
          inputRef={inputRef}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              search();
            }
          }}
          error={!!error && !selected}
          helperText={!selected && error ? error : "Press Enter or choose Find student."}
          autoComplete="off"
          sx={{ flex: 1 }}
        />
        <Button
          type="button"
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={search}
          disabled={result.loading || !query.trim()}
          sx={{ mt: { sm: 1 } }}
        >
          Find student
        </Button>
      </Stack>

      {result.loading && <LoadingState variant="inline" label="Finding students…" />}
      {result.error && <ErrorState compact error={result.error} onRetry={search} />}
      {result.users && !result.loading && (
        <>
          <Typography role="status" sx={{ color: "text.secondary", mb: 1 }}>
            {result.users.length === 0
              ? `No students match “${result.searched}”. Check the spelling, or search by email.`
              : `${result.users.length} ${result.users.length === 1 ? "student matches" : "students match"} “${result.searched}”.`}
          </Typography>
          {result.users.length > 0 && (
            <FormControl component="fieldset" sx={{ minWidth: 0, width: "100%" }}>
              <FormLabel component="legend" id="student-choice-label" sx={visuallyHidden}>
                Choose the student
              </FormLabel>
              <RadioGroup
                aria-labelledby="student-choice-label"
                value={selected?._id || ""}
                onChange={(e) => onSelect(result.users.find((u) => String(u._id) === e.target.value) || null)}
                sx={{ display: "grid", gap: 1 }}
              >
                {result.users.map((u) => {
                  const name = personName(u, u.email);
                  const chosen = String(selected?._id) === String(u._id);
                  return (
                    <FormControlLabel
                      key={u._id}
                      value={String(u._id)}
                      control={<Radio />}
                      label={
                        <Box sx={{ minWidth: 0 }}>
                          <Typography component="span" sx={{ display: "block", fontWeight: 600, overflowWrap: "anywhere" }}>
                            {name}
                          </Typography>
                          {u.email && u.email !== name && (
                            <Typography
                              component="span"
                              sx={{ display: "block", color: "text.secondary", overflowWrap: "anywhere" }}
                            >
                              {u.email}
                            </Typography>
                          )}
                        </Box>
                      }
                      sx={{ ...optionSx(chosen), alignItems: "center", py: chosen ? "7px" : "8px" }}
                    />
                  );
                })}
              </RadioGroup>
            </FormControl>
          )}
        </>
      )}

      {selected && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Logging a request for <strong>{personName(selected, selected.email)}</strong>
          {selected.email && selected.email !== personName(selected, selected.email) ? ` (${selected.email})` : ""}.
        </Alert>
      )}
    </Section>
  );
}

export default function AdminSupportLogPage() {
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [category, setCategory] = useState("");
  const [channel, setChannel] = useState("");
  const [description, setDescription] = useState("");
  const [reply, setReply] = useState("");
  const [resolve, setResolve] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const refs = useRef({});
  const alertRef = useRef(null);
  const register = (name) => (el) => {
    refs.current[name] = el;
  };

  const clear = (field) => setErrors((e) => (e[field] ? { ...e, [field]: undefined } : e));

  const validate = () => {
    const found = {};
    if (!student) found.student = "Find and choose the student first.";
    if (!category) found.category = "Choose the kind of help.";
    if (!channel) found.channel = "Choose how the student contacted you.";
    if (!description.trim()) found.description = "Write what the student asked for.";
    else if (description.length > MAX_TEXT) found.description = `Use ${MAX_TEXT} characters or fewer.`;
    if (reply.length > MAX_TEXT) found.reply = `Use ${MAX_TEXT} characters or fewer.`;
    return found;
  };

  const submit = async (e) => {
    e.preventDefault();
    setServerError(null);
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) {
      const first = ORDER.find((f) => found[f]) || "reply";
      const el = refs.current[first];
      const target = el && /^(INPUT|TEXTAREA)$/.test(el.tagName) ? el : el?.querySelector?.("input");
      target?.focus();
      return;
    }
    setSubmitting(true);
    try {
      const ticket = await fetchWithAuth("/api/admin/support", {
        method: "POST",
        body: JSON.stringify({
          userId: student._id,
          category,
          channel,
          description: description.trim(),
          ...(reply.trim() ? { reply: reply.trim() } : {}),
          resolve
        })
      });
      const ref = ticket?.reference ? ` ${ticket.reference}` : "";
      navigate(`/admin/support/${ticket._id}`, {
        state: {
          from: "/admin/support",
          notice: `Request${ref} logged. The student has been emailed the reference${resolve ? " and told it's resolved" : ""}.`
        }
      });
    } catch (err) {
      setServerError(err || "INTERNAL_ERROR");
      setTimeout(() => alertRef.current?.focus(), 0);
    } finally {
      setSubmitting(false);
    }
  };

  const breadcrumbs = [{ label: "Support requests", to: "/admin/support" }, { label: "Log a phone or email request" }];

  return (
    <Box>
      <PageHeader
        title="Log a phone or email request"
        breadcrumbs={breadcrumbs}
        description="Record a request a student made by phone or email. It gets a reference and the same 48-hour answer target as an online request, and the student is emailed a copy."
      />

      <Box component="form" noValidate onSubmit={submit} aria-label="Log a phone or email request" sx={{ maxWidth: 900 }}>
        <Stack spacing={2.5}>
          {serverError && (
            <Alert severity="error" role="alert" ref={alertRef} tabIndex={-1}>
              <AlertTitle>We couldn't log the request</AlertTitle>
              {errorMessage(serverError)}
            </Alert>
          )}

          <StudentPicker
            selected={student}
            onSelect={(u) => {
              setStudent(u);
              clear("student");
            }}
            error={errors.student}
            inputRef={register("student")}
          />

          <Section id="request" title="The request">
            <Stack spacing={3}>
              <FormControl component="fieldset" required error={!!errors.category} sx={{ minWidth: 0 }}>
                <FormLabel component="legend" id="log-category-label" sx={{ color: "text.primary", fontWeight: 650, mb: 1 }}>
                  Kind of help
                </FormLabel>
                <RadioGroup
                  ref={register("category")}
                  name="category"
                  aria-labelledby="log-category-label"
                  aria-describedby={errors.category ? "log-category-error" : undefined}
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    clear("category");
                  }}
                  sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" } }}
                >
                  {TICKET_CATEGORIES.map((c) => (
                    <OptionCard
                      key={c}
                      value={c}
                      selected={category === c}
                      Icon={categoryIcon(c)}
                      title={ticketCategoryLabel(c)}
                      hint={STAFF_HINT[c] || ticketCategoryHint[c]}
                    />
                  ))}
                </RadioGroup>
                {errors.category && <FormHelperText id="log-category-error">{errors.category}</FormHelperText>}
              </FormControl>

              <FormControl component="fieldset" required error={!!errors.channel} sx={{ minWidth: 0 }}>
                <FormLabel component="legend" id="log-channel-label" sx={{ color: "text.primary", fontWeight: 650, mb: 1 }}>
                  How the student contacted you
                </FormLabel>
                <RadioGroup
                  ref={register("channel")}
                  name="channel"
                  aria-labelledby="log-channel-label"
                  aria-describedby={errors.channel ? "log-channel-error" : undefined}
                  value={channel}
                  onChange={(e) => {
                    setChannel(e.target.value);
                    clear("channel");
                  }}
                  sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" } }}
                >
                  {LOGGED_CHANNELS.map((c) => (
                    <OptionCard key={c} value={c} selected={channel === c} Icon={channelIcon(c)} title={channelLabel(c)} />
                  ))}
                </RadioGroup>
                {errors.channel && <FormHelperText id="log-channel-error">{errors.channel}</FormHelperText>}
              </FormControl>

              <TextField
                id="log-description"
                label="What the student asked for"
                multiline
                minRows={4}
                required
                fullWidth
                value={description}
                inputRef={register("description")}
                onChange={(e) => {
                  setDescription(e.target.value);
                  clear("description");
                }}
                error={!!errors.description}
                helperText={errors.description || "In your own words. The student sees this in their support requests."}
                slotProps={{ htmlInput: { maxLength: MAX_TEXT } }}
              />
            </Stack>
          </Section>

          <Section
            id="answer"
            title="Your answer"
            description="Optional. If you already helped the student, write what you told them; it's added as a staff reply."
          >
            <Stack spacing={2}>
              <TextField
                id="log-reply"
                label="What you told the student"
                multiline
                minRows={3}
                fullWidth
                value={reply}
                inputRef={register("reply")}
                onChange={(e) => {
                  setReply(e.target.value);
                  clear("reply");
                }}
                error={!!errors.reply}
                helperText={
                  errors.reply ||
                  (category === "CONTENT"
                    ? "Course content answers need a Digital Driving Instructor registration on your account."
                    : "Optional")
                }
                slotProps={{ htmlInput: { maxLength: MAX_TEXT } }}
              />
              <FormControlLabel
                control={<Checkbox checked={resolve} onChange={(e) => setResolve(e.target.checked)} />}
                label="Mark as resolved: the student has what they needed"
              />
            </Stack>
          </Section>

          <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} sx={{ justifyContent: "flex-end" }}>
            <Button component={RouterLink} to="/admin/support" variant="outlined">
              Cancel
            </Button>
            <Button type="submit" variant="contained" startIcon={<SaveOutlinedIcon />} disabled={submitting}>
              {submitting ? "Logging the request…" : "Log the request"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
